import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import BalanceOutlinedIcon from "@mui/icons-material/BalanceOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import Button from "@mui/material/Button";

import { API_get_schedule_evaluation } from "../../../../../constants";
import { getRightworkAPI } from "../../../requests/utilities/requests";
import { getBusinessWeekStartDayIndex } from "../../dateRangePresets";
import WeekSelector from "../../../home/top_bar_components/WeekSelector";
import PrebuiltReportShell from "../PrebuiltReportShell";
import { unwrapPayload } from "./_shared/laborMath";

const GROUP_ICONS = {
  labor_fit: AssessmentOutlinedIcon,
  stability: GroupsOutlinedIcon,
  hours_equity: BalanceOutlinedIcon,
  ops_coverage: KeyOutlinedIcon,
};

const PROFILE_TONES = {
  labor_fit: "bg-cyan-50 text-cyan-800 border-cyan-200",
  stability: "bg-amber-50 text-amber-800 border-amber-200",
  hours_equity: "bg-violet-50 text-violet-800 border-violet-200",
  ops_coverage: "bg-emerald-50 text-emerald-800 border-emerald-200",
  balanced: "bg-slate-100 text-slate-700 border-slate-200",
  low_signal: "bg-rose-50 text-rose-800 border-rose-200",
  insufficient_data: "bg-slate-100 text-slate-500 border-slate-200",
};

function snapToWeekStart(date, weekStartDayIndex) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const diff = (d.getDay() - weekStartDayIndex + 7) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

function getWeekAnchorDate(weekStart) {
  const d = new Date(weekStart);
  d.setDate(d.getDate() + 3);
  return d;
}

function getStoreName(store) {
  return store?.store_name || store?.name || `Store ${store?.store_id || ""}`;
}

function getScoreTone(score) {
  if (score == null) return { fill: "#94a3b8", text: "text-slate-500" };
  if (score >= 80) return { fill: "#0f766e", text: "text-teal-700" };
  if (score >= 60) return { fill: "#d97706", text: "text-amber-700" };
  if (score >= 40) return { fill: "#2563eb", text: "text-blue-700" };
  return { fill: "#dc2626", text: "text-rose-700" };
}

function formatNumber(value, digits = 1) {
  if (value == null || Number.isNaN(Number(value))) return "-";
  return Number(value).toFixed(digits);
}

function formatPercent(value) {
  if (value == null || Number.isNaN(Number(value))) return "-";
  return `${Number(value).toFixed(1)}%`;
}

function formatHours(value) {
  if (value == null || Number.isNaN(Number(value))) return "-";
  return `${Number(value).toFixed(1)} hrs`;
}

function formatPriority(value) {
  if (value == null || Number.isNaN(Number(value))) return "-";
  return `${Number(value).toFixed(2)}x`;
}

function formatMetricValue(metric) {
  const value = metric?.value;
  if (value == null) return "No data";
  if (metric.format === "percent") return formatPercent(value);
  return formatNumber(value);
}

function formatScore(value) {
  if (value == null || Number.isNaN(Number(value))) return "-";
  return `${Math.round(Number(value))}`;
}

function ScoreBar({ metric }) {
  const score = metric?.score;
  const normalizedScore = Math.max(0, Math.min(100, Number(score) || 0));
  const tone = getScoreTone(score);
  const scoreExplanation = metric?.score_explanation || metric?.description || "";

  return (
    <div
      className="grid grid-cols-[190px_minmax(180px,1fr)_62px_82px] gap-3 items-center min-h-[34px] text-sm"
      title={scoreExplanation}
    >
      <div>
        <div className="font-medium text-slate-700 leading-tight">{metric.label}</div>
        <div className="text-[11px] text-slate-400 leading-tight">{formatMetricValue(metric)}</div>
      </div>
      <div className="relative h-3 rounded-full bg-slate-200">
        <div
          className="absolute left-0 top-0 h-3 rounded-full"
          style={{
            width: `${normalizedScore}%`,
            backgroundColor: tone.fill,
          }}
        />
        <div className="absolute left-1/4 top-[-5px] h-[22px] w-px bg-white/80" />
        <div className="absolute left-1/2 top-[-5px] h-[22px] w-px bg-white/80" />
        <div className="absolute left-3/4 top-[-5px] h-[22px] w-px bg-white/80" />
        {score != null && (
          <div
            className="absolute top-1/2 h-7 w-7 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white text-[11px] font-semibold text-white shadow-sm flex items-center justify-center"
            style={{
              left: `${normalizedScore}%`,
              backgroundColor: tone.fill,
            }}
          >
            {formatScore(score)}
          </div>
        )}
      </div>
      <div className="text-xs text-slate-400 text-center">score</div>
      <div className={`text-right text-sm font-semibold ${tone.text}`}>
        {score == null ? "-" : `${formatScore(score)}/100`}
      </div>
    </div>
  );
}

function CategorySummary({ category }) {
  const tone = getScoreTone(category?.score);
  return (
    <div className="border border-slate-200 rounded-lg px-3 py-2 bg-white">
      <div className="text-[11px] uppercase tracking-wide text-slate-400">
        {category.label}
      </div>
      <div className="mt-1 flex items-baseline justify-between gap-2">
        <span className={`text-lg font-semibold ${tone.text}`}>
          {category.score == null ? "-" : `${formatScore(category.score)}/100`}
        </span>
        <span className="text-xs text-slate-400">score</span>
      </div>
    </div>
  );
}

function StoreEvaluationVisual({ selectedStore, metricGroups }) {
  if (!selectedStore) {
    return (
      <div className="border border-slate-200 rounded-lg p-6 text-sm text-slate-500 bg-white">
        No store evaluation available for this week.
      </div>
    );
  }

  const profile = selectedStore.inferred_profile || {};
  const profileTone =
    PROFILE_TONES[profile.key] || "bg-slate-100 text-slate-700 border-slate-200";

  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-200 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-400">
            Schedule Evaluation
          </div>
          <h3 className="text-xl font-semibold text-slate-900 leading-tight">
            {selectedStore.store_name}
          </h3>
          <div className="mt-1 text-sm text-slate-500">
            {formatHours(selectedStore.scheduled_shift_hours)} scheduled -{" "}
            {selectedStore.total_shifts || 0} shifts -{" "}
            {formatHours(selectedStore.open_shift_hours)} open
          </div>
        </div>
        <div className={`border rounded-full px-3 py-1 text-sm font-semibold ${profileTone}`}>
          {profile.label || "Insufficient Data"}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 px-5 py-4 border-b border-slate-200 bg-slate-50">
        {Object.values(selectedStore.category_scores || {}).map((category) => (
          <CategorySummary key={category.key} category={category} />
        ))}
      </div>

      <div className="px-5 py-4">
        <div className="mb-3 text-xs text-slate-500">
          Scores are direct 0-100 evaluations from the underlying schedule metrics.
        </div>
        <div className="grid grid-cols-[190px_minmax(180px,1fr)_62px_82px] gap-3 items-center mb-2 text-xs font-semibold text-slate-400 uppercase tracking-wide">
          <div>Metric</div>
          <div className="grid grid-cols-3 text-center">
            <span>Poor</span>
            <span>Average</span>
            <span>Great</span>
          </div>
          <div />
          <div />
        </div>

        {metricGroups.map((group) => {
          const Icon = GROUP_ICONS[group.key] || AssessmentOutlinedIcon;
          return (
            <section key={group.key} className="mb-6 last:mb-0">
              <div className="flex items-center gap-2 pb-2 mb-3 border-b border-teal-700/50">
                <Icon sx={{ fontSize: 22, color: "#0f766e" }} />
                <h4 className="text-lg font-semibold text-slate-900">
                  {group.label}
                </h4>
              </div>
              <div className="space-y-2">
                {group.metrics.map((metricDef) => {
                  const metric = selectedStore.metrics?.[metricDef.key] || metricDef;
                  return <ScoreBar key={metricDef.key} metric={metric} />;
                })}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function EmployeeEquityTable({ employees }) {
  if (!employees?.length) return null;

  const rows = employees.slice(0, 12);

  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200">
        <h3 className="text-sm font-semibold text-slate-900">Employee Hours Equity</h3>
        <div className="text-xs text-slate-500">
          Priority-adjusted target-hour gaps for the selected store.
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left font-semibold px-4 py-2">Employee</th>
              <th className="text-right font-semibold px-4 py-2">Scheduled</th>
              <th className="text-right font-semibold px-4 py-2">Target</th>
              <th className="text-right font-semibold px-4 py-2">Fulfilled</th>
              <th className="text-right font-semibold px-4 py-2">Priority</th>
              <th className="text-right font-semibold px-4 py-2">Tenure</th>
              <th className="text-right font-semibold px-4 py-2">Prof.</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((employee) => (
              <tr
                key={employee.store_team_member_id}
                className="border-t border-slate-100"
              >
                <td className="px-4 py-2 font-medium text-slate-900">
                  {employee.employee_name || `Employee ${employee.store_team_member_id}`}
                  {employee.target_hours_source === "inferred" && (
                    <span className="ml-2 text-[11px] text-slate-400">inferred</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right text-slate-700">
                  {formatHours(employee.scheduled_hours)}
                </td>
                <td className="px-4 py-2 text-right text-slate-700">
                  {formatHours(employee.target_hours)}
                </td>
                <td className="px-4 py-2 text-right text-slate-700">
                  {formatPercent(employee.target_fulfillment_percent)}
                </td>
                <td className="px-4 py-2 text-right text-slate-700">
                  {formatPriority(employee.hours_priority_weight)}
                </td>
                <td className="px-4 py-2 text-right text-slate-700">
                  {employee.tenure_days == null
                    ? "-"
                    : `${Math.floor(Number(employee.tenure_days) / 30)} mo`}
                </td>
                <td className="px-4 py-2 text-right text-slate-700">
                  {formatNumber(employee.priority_proficiency_rating, 1)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StoreEvaluationTable({ stores, selectedStoreId, onSelectStore }) {
  const [sortKey, setSortKey] = useState("ops_coverage");

  const sortedStores = useMemo(() => {
    return [...stores].sort((a, b) => {
      const aValue =
        a.category_scores?.[sortKey]?.score ??
        a.metrics?.[sortKey]?.score ??
        -1;
      const bValue =
        b.category_scores?.[sortKey]?.score ??
        b.metrics?.[sortKey]?.score ??
        -1;
      return Number(bValue) - Number(aValue);
    });
  }, [stores, sortKey]);

  const columns = [
    { key: "labor_fit", label: "Labor" },
    { key: "stability", label: "Stability" },
    { key: "hours_equity", label: "Equity" },
    { key: "ops_coverage", label: "Ops" },
  ];

  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">All Stores</h3>
          <div className="text-xs text-slate-500">
            Direct scores out of 100 for the selected week.
          </div>
        </div>
        <select
          className="border border-slate-200 rounded-md px-2 py-1 text-sm bg-white"
          value={sortKey}
          onChange={(event) => setSortKey(event.target.value)}
        >
          {columns.map((column) => (
            <option key={column.key} value={column.key}>
              Sort by {column.label}
            </option>
          ))}
          <option value="priority_target_fulfillment_score">Sort by Priority Target</option>
          <option value="role_distribution_match_percent">Sort by Role Mix</option>
          <option value="opening_closing_consistency_score">Sort by Open/Close</option>
          <option value="schedule_consistency_score">Sort by Shift Repeat</option>
        </select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left font-semibold px-4 py-2">Store</th>
              <th className="text-left font-semibold px-4 py-2">Profile</th>
              {columns.map((column) => (
                <th key={column.key} className="text-right font-semibold px-4 py-2">
                  {column.label}
                </th>
              ))}
              <th className="text-right font-semibold px-4 py-2">Open Hrs</th>
            </tr>
          </thead>
          <tbody>
            {sortedStores.map((store) => {
              const selected = store.store_id === selectedStoreId;
              const profile = store.inferred_profile || {};
              return (
                <tr
                  key={store.store_id}
                  className={`border-t border-slate-100 cursor-pointer ${
                    selected ? "bg-teal-50" : "hover:bg-slate-50"
                  }`}
                  onClick={() => onSelectStore(store.store_id)}
                >
                  <td className="px-4 py-2 font-medium text-slate-900">
                    {getStoreName(store)}
                  </td>
                  <td className="px-4 py-2 text-slate-600">
                    {profile.label || "-"}
                  </td>
                  {columns.map((column) => (
                    <td key={column.key} className="px-4 py-2 text-right text-slate-700">
                      {store.category_scores?.[column.key]?.score == null
                        ? "-"
                        : formatScore(store.category_scores[column.key].score)}
                    </td>
                  ))}
                  <td className="px-4 py-2 text-right text-slate-700">
                    {formatNumber(store.open_shift_hours)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AutoscheduleEvaluationReport({
  report,
  onBack,
  homeStore,
  homeCompany,
  storeNames,
}) {
  const [selectedStoreId, setSelectedStoreId] = useState(homeStore || null);
  const weekStartStoreId = selectedStoreId || homeStore;
  const weekStartDayIndex = useMemo(
    () => getBusinessWeekStartDayIndex(storeNames, weekStartStoreId),
    [storeNames, weekStartStoreId]
  );
  const [weekStart, setWeekStart] = useState(() =>
    snapToWeekStart(new Date(), weekStartDayIndex)
  );
  const [baselineWeeks, setBaselineWeeks] = useState(6);
  const [payload, setPayload] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    setWeekStart((prev) =>
      snapToWeekStart(getWeekAnchorDate(prev), weekStartDayIndex)
    );
  }, [weekStartDayIndex]);

  useEffect(() => {
    if (homeStore) {
      setSelectedStoreId(homeStore);
    }
  }, [homeStore]);

  const weekEnd = useMemo(() => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + 6);
    return d;
  }, [weekStart]);

  const incrementWeek = useCallback((currentStart, direction) => {
    const next = new Date(currentStart);
    next.setDate(next.getDate() + direction * 7);
    setWeekStart(next);
  }, []);

  const setWeekStartDate = useCallback(
    (newDate) => {
      setWeekStart(snapToWeekStart(newDate, weekStartDayIndex));
    },
    [weekStartDayIndex]
  );

  useEffect(() => {
    const requestStoreId = selectedStoreId || homeStore || "";
    if (!homeCompany || !weekStart || !requestStoreId) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await getRightworkAPI(API_get_schedule_evaluation, {
          company_id: homeCompany,
          store_id: requestStoreId,
          week_start: dayjs(weekStart).format("YYYY-MM-DD"),
          selected_store_id: requestStoreId,
          baseline_weeks: baselineWeeks,
        });
        if (cancelled) return;
        setPayload(unwrapPayload(res));
      } catch (err) {
        if (cancelled) return;
        setError("Couldn't load schedule evaluation for this week.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [homeCompany, homeStore, selectedStoreId, weekStart, baselineWeeks, retryNonce]);

  const stores = useMemo(() => payload?.stores || [], [payload]);
  const selectedStore = useMemo(() => {
    if (!stores.length) return null;
    return (
      stores.find((store) => store.store_id === selectedStoreId) ||
      payload?.selected_store ||
      stores[0]
    );
  }, [stores, selectedStoreId, payload]);

  useEffect(() => {
    if (!selectedStoreId && payload?.selected_store_id) {
      setSelectedStoreId(payload.selected_store_id);
    }
  }, [payload, selectedStoreId]);

  const filters = (
    <div className="flex flex-wrap items-center gap-3">
      <WeekSelector
        start_date={weekStart}
        end_date={weekEnd}
        increment_week={incrementWeek}
        set_week_start_date={setWeekStartDate}
        businessWeekStartDayIndex={weekStartDayIndex}
      />
      <label className="flex items-center gap-2 text-sm text-slate-600">
        <span>Baseline</span>
        <select
          className="border border-slate-200 rounded-md px-2 py-1 text-sm bg-white"
          value={baselineWeeks}
          onChange={(event) => setBaselineWeeks(Number(event.target.value))}
        >
          <option value={4}>4 weeks</option>
          <option value={6}>6 weeks</option>
          <option value={8}>8 weeks</option>
          <option value={12}>12 weeks</option>
        </select>
      </label>
      <label className="flex items-center gap-2 text-sm text-slate-600">
        <span>Store</span>
        <select
          className="border border-slate-200 rounded-md px-2 py-1 text-sm bg-white min-w-[190px]"
          value={selectedStore?.store_id || selectedStoreId || ""}
          onChange={(event) => setSelectedStoreId(Number(event.target.value))}
        >
          {stores.map((store) => (
            <option key={store.store_id} value={store.store_id}>
              {getStoreName(store)}
            </option>
          ))}
        </select>
      </label>
    </div>
  );

  const actions = (
    <Button
      variant="outlined"
      size="small"
      startIcon={<RefreshIcon />}
      onClick={() => setRetryNonce((value) => value + 1)}
      sx={{
        borderColor: "#0f766e",
        color: "#0f766e",
        textTransform: "none",
        "&:hover": { borderColor: "#115e59", backgroundColor: "#f0fdfa" },
      }}
    >
      Refresh
    </Button>
  );

  return (
    <PrebuiltReportShell
      report={report}
      onBack={onBack}
      filters={filters}
      actions={actions}
      isLoading={isLoading}
    >
      {error ? (
        <div className="border border-rose-200 bg-rose-50 text-rose-700 rounded-lg px-4 py-3 text-sm">
          {error}
        </div>
      ) : (
        <div className="space-y-5">
          <StoreEvaluationVisual
            selectedStore={selectedStore}
            metricGroups={payload?.metric_groups || []}
          />
          <EmployeeEquityTable employees={selectedStore?.employee_metrics || []} />
          <StoreEvaluationTable
            stores={stores}
            selectedStoreId={selectedStore?.store_id}
            onSelectStore={setSelectedStoreId}
          />
        </div>
      )}
    </PrebuiltReportShell>
  );
}
